// Version: 2.0
// 2.0: Sales routes open to staff with the matching permission (requirePerm),
//      not admin-only. New /roles admin page for custom roles. ClientDefault
//      redirect sends a sales-only staff user to the pipeline.
// 1.9: Sales routes (admin only): /sales/search (Prospect Search) and
//      /sales/pipeline (Pipeline board). /sales redirects to the pipeline.
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import Login from './Login';
import MicrosoftCallback from './MicrosoftCallback';
import ChangePassword from './ChangePassword';
import MFAVerify from './MFAVerify';
import ActiveScans from './ActiveScans';
import ClientManagement from './ClientManagement';
import AuditLog from './AuditLog';
import ActivityLog from './ActivityLog';
import Admin from './Admin';
import Register from './Register';
import Shell from './Shell';
import Dashboard from './Dashboard';
import NewAssessment from './NewAssessment';
import ClientDetail from './ClientDetail';
import ClientDashboard from './ClientDashboard';
import Compliance from './Compliance';
import Schedules from './Schedules';
import Users from './Users';
import SaasReview from './SaasReview';
import Resources from './Resources';
import Productivity from './Productivity';
import Tickets from "./Tickets";
import EdgeNodes from "./EdgeNodes";
import NetworkDevices from "./NetworkDevices";
import AgentNodes from "./AgentNodes";
import PackEditor from "./PackEditor";
import AssetRegister from "./AssetRegister";
import SubscriptionTiers from "./SubscriptionTiers";
import SalesSearch from "./SalesSearch";
import Pipeline from "./Pipeline";
import Roles from "./Roles";

function PrivateRoute({ children, adminOnly = false, requirePerm = null }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><span className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.password_change_required) return <Navigate to="/change-password" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  if (requirePerm && user.role !== 'admin' && !(user.permissions || []).includes(requirePerm)) return <Navigate to="/" replace />;
  return <Shell>{children}</Shell>;
}

function ClientDefaultRedirect() {
  const { user } = useAuth();
  if (user?.role === 'client' && user?.client_slug) {
    return <Navigate to={`/clients/${user.client_slug}`} replace />;
  }
  if (user?.role === 'staff') {
    if ((user.permissions || []).includes('sales.view')) return <Navigate to="/sales/pipeline" replace />;
    return <Navigate to="/sales/search" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? (user.password_change_required ? <Navigate to="/change-password" /> : <Navigate to="/" />) : <Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/change-password" element={user ? <ChangePassword /> : <Navigate to="/login" />} />
      <Route path="/mfa" element={<MFAVerify />} />
      <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />

      {/* Admin dashboard */}
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />

      {/* Assessment tools */}
      <Route path="/new" element={<PrivateRoute><NewAssessment /></PrivateRoute>} />
      <Route path="/active" element={<PrivateRoute adminOnly><ActiveScans /></PrivateRoute>} />

      {/* Clients list — redirect to unified Client Management */}
      <Route path="/clients" element={<PrivateRoute><Navigate to="/manage" replace /></PrivateRoute>} />

      {/* /clients/:slug — tile dashboard for client role or as=client preview */}
      <Route path="/clients/:slug" element={
        <PrivateRoute>
          {(user?.role === 'client' || new URLSearchParams(window.location.search).get('as') === 'client' || sessionStorage.getItem('stg_as_client') === '1')
            ? <ClientDashboard />
            : <ClientDetail />}
        </PrivateRoute>
      } />

      {/* /clients/:slug/* — all hub routes (cyber-security, devices, compliance, etc.) */}
      <Route path="/clients/:slug/*" element={<PrivateRoute><ClientDetail /></PrivateRoute>} />

      {/* Compliance — dedicated page */}
      <Route path="/compliance" element={<PrivateRoute><Compliance /></PrivateRoute>} />

      {/* Admin common pages */}
      <Route path="/tickets" element={<PrivateRoute><Tickets user={user} /></PrivateRoute>} />
      <Route path="/schedules" element={<PrivateRoute><Schedules /></PrivateRoute>} />
      <Route path="/resources" element={<PrivateRoute><Resources /></PrivateRoute>} />
      <Route path="/productivity" element={<PrivateRoute><Productivity /></PrivateRoute>} />

      {/* Admin-only pages */}
      <Route path="/users" element={<PrivateRoute adminOnly><Users /></PrivateRoute>} />
      <Route path="/manage" element={<PrivateRoute adminOnly><ClientManagement /></PrivateRoute>} />
      <Route path="/asset-register" element={<PrivateRoute adminOnly><AssetRegister /></PrivateRoute>} />
      <Route path="/audit" element={<PrivateRoute adminOnly><AuditLog /></PrivateRoute>} />
      <Route path="/activity" element={<PrivateRoute adminOnly><ActivityLog /></PrivateRoute>} />
      <Route path="/admin" element={<PrivateRoute adminOnly><Admin /></PrivateRoute>} />
      <Route path="/saas" element={<PrivateRoute adminOnly><SaasReview /></PrivateRoute>} />
      <Route path="/edge-nodes" element={<PrivateRoute adminOnly><EdgeNodes /></PrivateRoute>} />
      <Route path="/agent-nodes" element={<PrivateRoute adminOnly><AgentNodes /></PrivateRoute>} />
      <Route path="/pack-editor" element={<PrivateRoute adminOnly><PackEditor /></PrivateRoute>} />
      <Route path="/subscription-tiers" element={<PrivateRoute adminOnly><SubscriptionTiers /></PrivateRoute>} />
      <Route path="/network" element={<PrivateRoute><NetworkDevices /></PrivateRoute>} />

      {/* Sales and Business Development — admins and staff with sales permissions */}
      <Route path="/sales" element={<PrivateRoute><Navigate to="/sales/pipeline" replace /></PrivateRoute>} />
      <Route path="/sales/search" element={<PrivateRoute requirePerm="sales.search"><SalesSearch /></PrivateRoute>} />
      <Route path="/sales/pipeline" element={<PrivateRoute requirePerm="sales.view"><Pipeline user={user} /></PrivateRoute>} />
      <Route path="/roles" element={<PrivateRoute adminOnly><Roles /></PrivateRoute>} />

      {/* Default redirect */}
      <Route path="*" element={<PrivateRoute><ClientDefaultRedirect /></PrivateRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}